import memoize from 'lodash.memoize';
import {
  getSystemInfo,
  type SystemInfo,
  OS,
  BrowserType,
} from '@doxyme/ui-foundation/hooks/useBounds/getSystemInfo';

type OperatingSystemConfig = {
  name: OS;
  version?: number; // The disallowed major version
  minVersion?: number; // The minimum disallowed major version and higher (inclusive)
  maxVersion?: number; // The maximum disallowed major version ans lower (exclusive)
};

type DeviceConfig = {
  operatingSystem: OperatingSystemConfig[];
};

/**
 * Config for devices that are not capable of supporting HD quality
 */
const HDVideoUnsupportedDeviceConfig: DeviceConfig = {
  operatingSystem: [{ name: OS.IOS, maxVersion: 15 }, { name: OS.ChromeOS }],
};

/**
 * Checks if the device supports HD video quality based on a passed config
 * @param config {DeviceConfig} - config for devices that are not capable of supporting HD quality
 * @returns {boolean}
 */
export const isHDVideoSupportedImpl = (config: DeviceConfig): boolean => {
  const { os: userOS, osVersion, deviceType } = getSystemInfo();
  const userOSVersion = getMajorOSVersion(osVersion);

  if (deviceType === BrowserType.Mobile) {
    return false;
  }

  for (const osConfig of config.operatingSystem) {
    const { name, version, minVersion, maxVersion } = osConfig;

    if (name === userOS) {
      // If both minVersion and maxVersion are undefined, disallow all versions
      if (
        version === undefined &&
        minVersion === undefined &&
        maxVersion === undefined
      ) {
        return false;
      }

      // Check against version, minVersion and maxVersion
      if (
        (version !== undefined && userOSVersion === version) ||
        (minVersion !== undefined && userOSVersion >= minVersion) ||
        (maxVersion !== undefined && userOSVersion <= maxVersion)
      ) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Checks if the device supports HD video quality based on HDVideoUnsupportedDeviceConfig
 * @returns {boolean} - Memoized value
 */
export const isHDVideoSupported: () => boolean = memoize(() =>
  isHDVideoSupportedImpl(HDVideoUnsupportedDeviceConfig),
);

const getMajorOSVersion = (osVersion: SystemInfo['osVersion']): number =>
  Number(osVersion.split('.')[0]);
